import React, { useEffect, useMemo, useState } from 'react';
import './Chatbot.css';
import {
  schoolSyllabusList,
  schoolSubjectList,
  collegeUniversityList,
  collegeProgramList,
  collegeStreamMap,
  collegeLevelMap,
  collegeSubjectMap,
} from './chatbotUtils';
import Lottie from 'lottie-react';
import generateAnswerAnim from '../../animations/generate_ai_answer.json';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import API_BASE_URL from "../../config";
import { trackEvent } from '../../utils/analytics';

const ChatbotModal = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState('');
  const [syllabus, setSyllabus] = useState('');
  const [grade, setGrade] = useState('');
  const [subject, setSubject] = useState('');
  const [question, setQuestion] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [program, setProgram] = useState('');
  const [stream, setStream] = useState('');
  const [level, setLevel] = useState('');

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const totalSteps = category === 'college' ? 6 : category === 'school' ? 4 : 1;

  const levelOptions = useMemo(() => collegeLevelMap[program] || [], [program]);
  const streamOptions = useMemo(() => collegeStreamMap[program] || [], [program]);
  const collegeSubjects = useMemo(() => {
    if (!program || !stream) return [];
    return collegeSubjectMap[program]?.[stream] || ['Ask Anything'];
  }, [program, stream]);

  const getOptionClassName = (item) =>
    item === 'Ask Anything' ? 'option-button option-button-featured' : 'option-button';

  useEffect(() => {
    if (category) {
      trackEvent("chatbot_path_selected", {
        category,
        syllabus: syllabus || undefined
      });
    }
  }, [category, syllabus]);

  const resetConversation = () => {
    setQuestion('');
    setResponse('');
    setError('');
    setLoading(false);
  };

  const startListening = () => {
    if (!('webkitSpeechRecognition' in window)) {
      alert('Speech recognition is not supported in this browser. Please use Chrome or Edge.');
      return;
    }
    trackEvent("chatbot_voice_input_started", { category });

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-IN';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.start();
    setIsListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setQuestion(prev => prev ? `${prev} ${transcript}` : transcript);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };
  };

  const handleSubmit = async () => {
    const contextLabel = category === 'college'
      ? `${syllabus} ${program} ${stream} ${level} ${subject}`
      : `${syllabus} grade ${grade} ${subject}`;

    if (!question || !syllabus || !subject || (category === 'school' && !grade) || (category === 'college' && (!program || !stream || !level))) {
      setError('Please complete all fields before submitting.');
      return;
    }

    setError('');
    setLoading(true);
    setResponse('');
    trackEvent("chatbot_question_submitted", {
      category,
      syllabus,
      subject,
      grade: category === 'school' ? grade : undefined,
      program: category === 'college' ? program : undefined,
      stream: category === 'college' ? stream : undefined,
      level: category === 'college' ? level : undefined
    });

    const prompt = category === 'college'
      ? `Please explain the below question in the context of '${syllabus}' ${program}, ${stream}, ${level}, subject '${subject}'. Keep the explanation clear, student-friendly, and aligned with the expected syllabus. You can include image or video references if useful.\n\nQuestion: ${question}`
      : `Please explain the below question in the context of '${syllabus}' ${grade}-th grade ${subject} paper. The answer should be easily understandable by the ${grade}-th grade student and should match the syllabus for the grade. You can give image or video references, if required.\n\nQuestion: ${question}`;

    try {
      const apiResponse = await fetch(`${API_BASE_URL}/api/get-chat-explanation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, context: contextLabel }),
      });

      const data = await apiResponse.json();
      const answer = data?.answer;

      if (!answer) {
        setResponse('Sorry, could not fetch a valid answer. Please try again.');
        trackEvent("chatbot_response_failed", {
          category,
          subject,
          reason: "empty_answer"
        });
      } else {
        setResponse(answer);
        trackEvent("chatbot_response_received", {
          category,
          subject,
          syllabus
        });
      }
    } catch (err) {
      console.error('Error fetching explanation:', err);
      setResponse('An error occurred while fetching the explanation. Please try again later.');
      trackEvent("chatbot_response_failed", {
        category,
        subject,
        reason: err?.message || "request_failed"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setError('');
    if (category === 'college') {
      if (step === 6) setStep(5);
      else if (step === 5) setStep(4);
      else if (step === 4) setStep(3);
      else if (step === 3) setStep(2);
      else if (step === 2) setStep(1);
      else {
        setCategory('');
        setStep(1);
      }
      return;
    }

    if (step > 1) {
      setStep(step - 1);
    } else {
      setCategory('');
      setStep(1);
    }
  };

  return (
    <div className="chatbot-overlay" onClick={onClose}>
      <div
        className="chatbot-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="ZenQB chat assistant"
      >
        <div className="chatbot-header">
          <div>
            <p className="chatbot-eyebrow">ZenQB Assistant</p>
            <h2 className="chatbot-title">Ask anything about your syllabus</h2>
          </div>
          <button className="close-button" onClick={onClose} aria-label="Close chatbot">
            x
          </button>
        </div>

        <div className="chatbot-subheader">
          <span className="chatbot-step-badge">Step {Math.min(step, totalSteps)} of {totalSteps}</span>
          <p className="chatbot-hint">Click outside or press `Esc` to close.</p>
        </div>

        <div className="chatbot-body">
          {step === 1 && (
            <div className="chat-step">
              <h3>Select your learning path</h3>
              <div className="chatbot-section-grid">
                <div className="chatbot-section-card">
                  <div className="chatbot-section-head">
                    <h4>School</h4>
                    <p>Keep the existing school flow exactly as before.</p>
                  </div>
                  <div className="options-grid">
                    {schoolSyllabusList.map((item) => (
                      <button
                        key={item}
                        className="option-button"
                        onClick={() => {
                          setCategory('school');
                          setSyllabus(item);
                          setGrade('');
                          setSubject('');
                          setProgram('');
                          setStream('');
                          setLevel('');
                          resetConversation();
                          setStep(2);
                        }}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="chatbot-section-card">
                  <div className="chatbot-section-head">
                    <h4>College</h4>
                    <p>Choose your university first, then narrow down program, stream, and semester.</p>
                  </div>
                  <div className="options-grid">
                    {collegeUniversityList.map((item) => (
                      <button
                        key={item}
                        className="option-button"
                        onClick={() => {
                          setCategory('college');
                          setSyllabus(item);
                          setGrade('');
                          setSubject('');
                          setProgram('');
                          setStream('');
                          setLevel('');
                          resetConversation();
                          setStep(2);
                        }}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {category === 'school' && step === 2 && (
            <div className="chat-step">
              <h3>Select your grade</h3>
              <div className="options-grid">
                {[...Array(10)].map((_, i) => (
                  <button
                    key={i + 1}
                    className="option-button"
                    onClick={() => {
                      setGrade(i + 1);
                      setStep(3);
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>
          )}

          {category === 'school' && step === 3 && (
            <div className="chat-step">
              <h3>Select subject</h3>
              <div className="options-grid">
                {schoolSubjectList.map((item) => (
                  <button
                    key={item}
                    className={getOptionClassName(item)}
                    onClick={() => {
                      setSubject(item);
                      setStep(4);
                    }}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          )}

          {category === 'college' && step === 2 && (
            <div className="chat-step">
              <h3>Select your course</h3>
              <div className="options-grid">
                {collegeProgramList.map((item) => (
                  <button
                    key={item}
                    className="option-button"
                    onClick={() => {
                      setProgram(item);
                      setStream('');
                      setLevel('');
                      setSubject('');
                      setStep(3);
                    }}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          )}

          {category === 'college' && step === 3 && (
            <div className="chat-step">
              <h3>Select stream</h3>
              <div className="options-grid">
                {streamOptions.map((item) => (
                  <button
                    key={item}
                    className="option-button"
                    onClick={() => {
                      setStream(item);
                      setLevel('');
                      setSubject('');
                      setStep(4);
                    }}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          )}

          {category === 'college' && step === 4 && (
            <div className="chat-step">
              <h3>Select semester or year</h3>
              <div className="options-grid">
                {levelOptions.map((item) => (
                  <button
                    key={item}
                    className="option-button"
                    onClick={() => {
                      setLevel(item);
                      setSubject('');
                      setStep(5);
                    }}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          )}

          {category === 'college' && step === 5 && (
            <div className="chat-step">
              <h3>Select subject</h3>
              <div className="options-grid">
                {collegeSubjects.map((item) => (
                  <button
                    key={item}
                    className={getOptionClassName(item)}
                    onClick={() => {
                      setSubject(item);
                      setStep(6);
                    }}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          )}

          {((category === 'school' && step === 4) || (category === 'college' && step === 6)) && (
            <div className="chat-step">
              <h3>Ask or type your question</h3>
              <div className="chatbot-context-row">
                <span>{syllabus}</span>
                {category === 'school' ? <span>Grade {grade}</span> : <span>{program}</span>}
                {category === 'college' && <span>{stream}</span>}
                {category === 'college' && <span>{level}</span>}
                <span>{subject}</span>
              </div>
              <div className="chatbot-input-wrap">
                <textarea
                  className="chatbot-textarea"
                  placeholder="Type your question here, or tap the microphone to speak."
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  rows={5}
                />
                <button
                  onClick={startListening}
                  className={`mic-button ${isListening ? 'listening' : ''}`}
                  title={isListening ? 'Listening...' : 'Click to speak'}
                >
                  🎤
                </button>
              </div>
              {error && <div className="error-msg">{error}</div>}
              <div className="chatbot-actions">
                <button className="secondary-button" onClick={handleBack}>
                  Back
                </button>
                <button className="submit-button" onClick={handleSubmit}>
                  Get Explanation
                </button>
              </div>
            </div>
          )}

          {!loading && step > 1 && !(((category === 'school' && step === 4) || (category === 'college' && step === 6))) && (
            <div className="chatbot-inline-actions">
              <button className="secondary-button" onClick={handleBack}>
                Back
              </button>
            </div>
          )}

          {loading && (
            <div className="chat-step chatbot-loading">
              <h3>Generating answer...</h3>
              <Lottie animationData={generateAnswerAnim} loop style={{ height: 150 }} />
            </div>
          )}

          {!loading && response && (
            <div className="chat-step">
              <h3>Explanation</h3>
              <div className="response-box chatbot-markdown">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkMath]}
                  rehypePlugins={[rehypeKatex]}
                  components={{
                    h2: ({children}) => {
                      const text = String(children);
                      const isAnswer = /correct answer|✅|final answer/i.test(text);
                      return (
                        <h3 style={{
                          fontSize: '14px',
                          fontWeight: 700,
                          color: isAnswer ? '#059669' : '#111827',
                          marginTop: '14px',
                          marginBottom: '6px',
                          fontFamily: "'Space Grotesk', sans-serif",
                          background: isAnswer ? '#ecfdf5' : 'transparent',
                          padding: isAnswer ? '8px 12px' : '0',
                          borderRadius: isAnswer ? '8px' : '0',
                        }}>{children}</h3>
                      );
                    },
                    h3: ({children}) => (
                      <h4 style={{ fontSize: '13px', fontWeight: 600, color: '#374155', marginTop: '10px', marginBottom: '4px', fontFamily: "'Space Grotesk', sans-serif" }}>{children}</h4>
                    ),
                    p: ({children}) => <p style={{ marginBottom: '8px', lineHeight: '1.65', color: '#374151', fontSize: '13.5px' }}>{children}</p>,
                    ul: ({children}) => <ul style={{ listStyle: 'disc', paddingLeft: '18px', marginBottom: '8px', color: '#374151', fontSize: '13.5px' }}>{children}</ul>,
                    ol: ({children}) => <ol style={{ listStyle: 'decimal', paddingLeft: '18px', marginBottom: '8px', color: '#374151', fontSize: '13.5px' }}>{children}</ol>,
                    li: ({children}) => <li style={{ marginBottom: '3px', lineHeight: '1.6' }}>{children}</li>,
                    strong: ({children}) => <strong style={{ color: '#111827', fontWeight: 700 }}>{children}</strong>,
                    em: ({children}) => <em style={{ color: '#6b7280' }}>{children}</em>,
                    blockquote: ({children}) => {
                      const text = String(children);
                      if (text.includes('💡') || /exam tip/i.test(text)) {
                        return (
                          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '10px 12px', margin: '10px 0', fontSize: '12.5px', color: '#92400e' }}>
                            {children}
                          </div>
                        );
                      }
                      return (
                        <blockquote style={{ borderLeft: '3px solid #6366f1', paddingLeft: '10px', margin: '10px 0', color: '#4b5563', background: '#f9fafb', borderRadius: '0 6px 6px 0', padding: '8px 10px', fontSize: '13px' }}>
                          {children}
                        </blockquote>
                      );
                    },
                    code: ({inline, className, children, ...props}) => {
                      if (inline) {
                        return <code style={{ background: '#f1f5f9', color: '#6366f1', padding: '1px 5px', borderRadius: '3px', fontSize: '12px', fontFamily: 'monospace' }} {...props}>{children}</code>;
                      }
                      return (
                        <div style={{ margin: '8px 0', borderRadius: '6px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                          <pre style={{ margin: 0, padding: '10px', background: '#f9fafb', overflowX: 'auto' }}>
                            <code style={{ fontSize: '12px', fontFamily: 'monospace', color: '#374151', lineHeight: '1.5' }} {...props}>{children}</code>
                          </pre>
                        </div>
                      );
                    },
                    table: ({children}) => (
                      <div style={{ margin: '10px 0', overflowX: 'auto', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                        <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>{children}</table>
                      </div>
                    ),
                    thead: ({children}) => <thead style={{ background: '#f3f4f6' }}>{children}</thead>,
                    th: ({children}) => <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#111827', borderBottom: '1px solid #e5e7eb', fontSize: '11px' }}>{children}</th>,
                    td: ({children}) => <td style={{ padding: '6px 10px', color: '#4b5563', borderBottom: '1px solid #f3f4f6' }}>{children}</td>,
                    hr: () => <hr style={{ margin: '10px 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />,
                  }}
                >
                  {response}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatbotModal;
